import React, { useState, useEffect, useReducer } from "react";
import { Link, useLocation } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import Collapse from "react-bootstrap/Collapse";
import { MenuListArray2 } from "./MenuListArray2";
import { useTranslation } from "react-i18next";
import "../assets/css/header2.css";

/* ── Language data ──────────────────────── */
const LANGUAGES = [
  { code: "EN", label: "English", flag: "🇺🇸" },
  { code: "AR", label: "العربية", flag: "🇸🇦" },
];

const Header2 = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const [activeLang, setActiveLang] = useState(() => {
    const currentLang = i18n.language?.toLowerCase().startsWith("ar") ? "AR" : "EN";
    return LANGUAGES.find((lang) => lang.code === currentLang) || LANGUAGES[0];
  });

  /* Submenu state */
  const reducer = (prev, next) => ({ ...prev, ...next });
  const [state, setState] = useReducer(reducer, { active: "", activeSubmenu: "" });

  /* Scroll watcher */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close lang drop on outside click */
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".hd-lang")) setLangOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* Sync active language on load + on language change */
  useEffect(() => {
    const currentLang = i18n.language?.toLowerCase().startsWith("ar") ? "AR" : "EN";
    const matchedLang =
      LANGUAGES.find((lang) => lang.code === currentLang) || LANGUAGES[0];

    setActiveLang(matchedLang);
    document.documentElement.lang = currentLang.toLowerCase();
    document.documentElement.dir = currentLang === "AR" ? "rtl" : "ltr";
  }, [i18n.language]);

  /* Active route */
  useEffect(() => {
    let foundMenu = "";

    MenuListArray2?.forEach((el) => {
      if (el.to === location.pathname) foundMenu = el.title;
      el.content?.forEach((sub) => {
        if (sub.to === location.pathname) foundMenu = el.title;
      });
    });

    setActiveMenu(foundMenu);
  }, [location.pathname]);

  const handleMenuActive = (title) =>
    setState({ active: state.active === title ? "" : title });

  const handleSubmenuActive = (title) =>
    setState({ activeSubmenu: state.activeSubmenu === title ? "" : title });

  const toggleLanguage = () => {
    const newLang = i18n.language?.toLowerCase().startsWith("ar") ? "en" : "ar";
    i18n.changeLanguage(newLang);
  };

  const handleLangSelect = (lang) => {
    const selectedLang = lang.code.toLowerCase();
    const currentLang = i18n.language?.toLowerCase().startsWith("ar") ? "ar" : "en";

    setLangOpen(false);

    if (selectedLang === currentLang) {
      return;
    }

    setActiveLang(lang);
    i18n.changeLanguage(selectedLang);
  };

  return (
    <>
      <header
        className={`hd-header site-header mo-left header header-transparent style-2 ${scrolled ? "hd-fixed is-fixed" : "hd-transparent"
          }`}
        style={{ zIndex: 1000 }}
      >
        <div className="container">
          <div className="hd-bar main-bar clearfix">

            <Link to="/" className="hd-logo logo-header">
              <img
                src={scrolled ? IMAGES.logoHeader : IMAGES.logoWhiteHeader}
                alt="FitZone"
                className="select_logo"
                fetchpriority="high"
                width="200"
              />
            </Link>

            <ul className="hd-nav navbar-nav nav">
              {MenuListArray2.map((item, index) => {
                const hasDropdown =
                  item.classChange === "sub-menu-down" && item.content?.length > 0;

                return (
                  <li
                    key={index}
                    className={`${item.classChange || ""} ${item.title === activeMenu ? "active" : ""
                      } ${state.active === item.title || state.active === t(item.title)
                        ? "open"
                        : ""
                      }`}
                  >
                    {hasDropdown ? (
                      <>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleMenuActive(item.title);
                          }}
                        >
                          {t(item.title)}
                          <i className="hd-caret fa fa-angle-down" />
                        </Link>

                        <ul className="hd-dropdown sub-menu">
                          {item.content.map((sub, si) => (
                            <li
                              key={si}
                              className={
                                state.activeSubmenu === sub.title ||
                                  state.activeSubmenu === t(sub.title)
                                  ? "open"
                                  : ""
                              }
                            >
                              {sub.content?.length > 0 ? (
                                <>
                                  <Link
                                    to={sub.to}
                                    onClick={() => handleSubmenuActive(sub.title)}
                                  >
                                    {t(sub.title)}
                                    <i
                                      className={`fa-solid ${document?.documentElement?.dir === "rtl"
                                        ? "fa-angle-left"
                                        : "fa-angle-right"
                                        }`}
                                    />
                                  </Link>

                                  <ul className="hd-subdrop sub-menu">
                                    {sub.content.map((deep, di) => (
                                      <li key={di}>
                                        <Link to={deep.to}>{t(deep.title)}</Link>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <Link to={sub.to}>{t(sub.title)}</Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <Link to={item.to}>{t(item.title)}</Link>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="hd-actions">

              <div className={`hd-lang ${langOpen ? "open" : ""}`}>
                <button
                  className="hd-lang-toggle"
                  onClick={() => setLangOpen(!langOpen)}
                  aria-label={`Switch language: currently ${activeLang.label}`}
                  type="button"
                >
                  <span className="hd-lang-flag">{activeLang.flag}</span>
                  <span className="hd-lang-code">{activeLang.code}</span>
                  <i className="hd-lang-caret fa fa-angle-down" />
                </button>

                <ul className="hd-lang-drop">
                  {LANGUAGES.map((lang) => (
                    <li
                      key={lang.code}
                      className={activeLang.code === lang.code ? "active" : ""}
                      onClick={() => handleLangSelect(lang)}
                    >
                      <span className="hd-flag">{lang.flag}</span>
                      <span className="hd-lang-name">{lang.label}</span>
                      <span className="hd-lang-code-sm">{lang.code}</span>
                      {activeLang.code === lang.code && (
                        <i className="hd-check fa-solid fa-check" />
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <Link to="/contact-us" className="hd-cta-btn">
                {t("header.joinNow")}
                <i
                  className={`fa-solid ${document?.documentElement?.dir === "rtl"
                    ? "fa-arrow-left"
                    : "fa-arrow-right"
                    }`}
                />
              </Link>

              <button
                className={`hd-toggler navbar-toggler navicon ${mobileOpen ? "open" : "collapsed"
                  }`}
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
                type="button"
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className={`hd-mobile-menu ${mobileOpen ? "open show" : ""}`} id="OpenMenu">
        <div className="hd-mob-inner">
          {MenuListArray2.map((item, index) => {
            const hasChildren = item.content?.length > 0;
            const isOpen = state.active === item.title;

            return (
              <div
                key={index}
                className={`hd-mob-item ${isOpen ? "open" : ""}`}
              >
                <Link
                  to={hasChildren ? "#" : item.to}
                  className={`hd-mob-link ${item.title === activeMenu || t(item.title) === activeMenu ? "active" : ""
                    }`}
                  onClick={(e) => {
                    if (hasChildren) {
                      e.preventDefault();
                      handleMenuActive(item.title);
                    } else {
                      setMobileOpen(false);
                    }
                  }}
                >
                  {t(item.title)}
                  {hasChildren && <i className="hd-mob-caret fa fa-angle-down" />}
                </Link>

                {hasChildren && (
                  <Collapse in={isOpen}>
                    <ul className="hd-mob-submenu sub-menu">
                      {item.content.map((sub, si) => (
                        <li key={si}>
                          <Link to={sub.to} onClick={() => setMobileOpen(false)}>
                            {t(sub.title)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Collapse>
                )}
              </div>
            );
          })}

          <div className="hd-mob-bottom">
            <div className="hd-mob-langs">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className={`hd-mob-lang-btn ${activeLang.code === lang.code ? "active" : ""
                    }`}
                  onClick={() => handleLangSelect(lang)}
                >
                  {lang.flag} {lang.code}
                </button>
              ))}
            </div>

            <Link
              to="/contact-us"
              className="hd-mob-cta"
              onClick={() => setMobileOpen(false)}
            >
              {t("header.joinNow")}
              <i
                className={`fa-solid ${document?.documentElement?.dir === "rtl"
                  ? "fa-arrow-left"
                  : "fa-arrow-right"
                  }`}
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header2;
